import React, { Dispatch, SetStateAction } from 'react';
import CardLayout from '../../../../shared/components/card/CardLayout';
import { formatDate } from '../../../../shared/functions/Functions';
import { priceSplitter } from '../../../../utils/helpers/priceSplitter';
import { Button, Dropdown, Image } from 'react-bootstrap';
import { IInvoiceSchedules } from '../../../../model/shared/IContract';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaEllipsisV } from 'react-icons/fa';
import { FaRegTrashCan } from 'react-icons/fa6';
import { Fancybox } from '../../../../shared/components/Fancybox';

export interface InvoiceSubCardProps {
    invoice: IInvoiceSchedules;
    length: number;
    openUploadModal: (
        invoiceId: number,
        sequenceNumber: number,
        file?: string,
        rejectReason?: string,
        isLongTermOffer?: boolean,
        selectedCampaignId?: number,
        numberInvoices?: number,
        campaignTitle?: string
    ) => void;
    openDeleteModal: Dispatch<SetStateAction<{ id: number; show: boolean; title: string }>>;
    downloadInvoice: (id: number) => void;
    selectedCampaignId?: number;
    selectedCampaignTitle?: string;
}

const InvoiceSubCard = ({
    invoice,
    length,
    openUploadModal,
    openDeleteModal,
    downloadInvoice,
    selectedCampaignId,
    selectedCampaignTitle,
}: InvoiceSubCardProps) => {
    const showDropdown = !invoice?.status || !['missing'].includes(invoice?.status ?? '');
    const { t } = useTranslation();

    const mainButtonType = (status: string) => {
        switch (status) {
            case 'missing':
                return (
                    <Button
                        variant="primary"
                        onClick={() =>
                            openUploadModal(
                                invoice.id,
                                invoice.sequenceNumber,
                                invoice?.invoice?.path,
                                invoice?.invoice?.rejectReason,
                                undefined,
                                selectedCampaignId,
                                length,
                                selectedCampaignTitle
                            )
                        }
                    >
                        {t('general.button.upload')}
                    </Button>
                );
            case 'approved':
            case 'paid':
            case 'verified':
                return (
                    <div className="d-inline-block">
                        <Fancybox>
                            <div
                                data-fancybox="content-gallery"
                                className="cursor-pointer"
                                data-src={invoice?.invoice?.path ?? '/icons/filePlaceholder.png'}
                            >
                                <Button variant="outline-primary">{t('general.button.open')}</Button>
                            </div>
                        </Fancybox>
                    </div>
                );
            case 'rejected':
                return (
                    <Button
                        onClick={() =>
                            openUploadModal(
                                invoice?.invoice?.id ?? 0,
                                invoice.sequenceNumber,
                                invoice?.invoice?.path,
                                invoice?.invoice?.rejectReason,
                                undefined,
                                undefined,
                                length,
                                selectedCampaignTitle
                            )
                        }
                        variant="primary"
                    >
                        {t('general.button.replace')}
                    </Button>
                );
            case 'being_verified':
                return (
                    <div className="d-inline-block">
                        <Fancybox>
                            <div
                                data-fancybox="content-gallery"
                                className="cursor-pointer"
                                data-src={invoice?.invoice?.path ?? '/icons/filePlaceholder.png'}
                            >
                                <Button variant="outline-primary">{t('general.button.open')}</Button>
                            </div>
                        </Fancybox>
                    </div>
                );
            default:
                return null;
        }
    };

    const dropdownButtonType = (status: string) => {
        switch (status) {
            case 'missing':
                return null;
            case 'approved':
            case 'paid':
            case 'verified':
                return (
                    <Dropdown.Item
                        className="d-flex align-items-center text-muted"
                        onClick={() => downloadInvoice(invoice?.invoice?.id ?? 0)}
                    >
                        <FaDownload className="mr-1" />
                        {t('general.download')}
                    </Dropdown.Item>
                );
            case 'rejected':
                return (
                    <Fancybox>
                        <div
                            data-fancybox="content-gallery"
                            className="cursor-pointer"
                            data-src={invoice?.invoice?.path ?? '/icons/filePlaceholder.png'}
                        >
                            <Dropdown.Item
                                target="_blank"
                                rel="noreferrer"
                                href={invoice.invoice?.path}
                                className="d-flex align-items-center text-muted textHover"
                            >
                                <Image className="mr-1" alt="image-down" src="/icons/img-open-file.svg" />
                                {t('general.button.open')}
                            </Dropdown.Item>
                        </div>
                    </Fancybox>
                );

            case 'being_verified':
                return (
                    <>
                        <Dropdown.Item
                            onClick={() =>
                                openDeleteModal(prevState => ({
                                    ...prevState,
                                    id: invoice?.invoice?.id ?? 0,
                                    show: true,
                                }))
                            }
                            className="text-danger d-flex align-items-center"
                        >
                            <FaRegTrashCan className="mr-1" />
                            {t('general.button.delete')}
                        </Dropdown.Item>
                        <Dropdown.Item
                            className="d-flex align-items-center text-muted"
                            onClick={() => downloadInvoice(invoice?.invoice?.id ?? 0)}
                        >
                            <FaDownload className="mr-1" />
                            {t('general.download')}
                        </Dropdown.Item>
                    </>
                );
            default:
                return null;
        }
    };

    const getStatusIconPath = (status: string) => {
        switch (status) {
            case 'missing':
                return 'approval-status/pending.svg';
            case 'paid':
                return 'approval-status/paid.svg';
            case 'approved':
            case 'verified':
                return 'approval-status/approved.svg';
            case 'being_verified':
                return 'being-verified-icon.svg';
            case 'rejected':
                return 'approval-status/rejected.svg';

            default:
                return null;
        }
    };

    return (
        <>
            <CardLayout classCard="invoice-sub-card mt-2" classBody="invoice-sub-card-body px-3 pt-2 pb-0 py-md-0 ">
                <div className="card-content">
                    <strong>
                        {t('influencer.invoice.invoice')} {invoice?.sequenceNumber ?? 0}/{length ?? 0}
                    </strong>
                </div>
                <div className="card-info">
                    <div className="info-block d-flex justify-content-start text-start ">
                        {!!invoice.status && (
                            <img
                                width={40}
                                height={40}
                                src={`/icons/${getStatusIconPath(invoice?.status ?? '')}`}
                                alt="approval-status"
                            />
                        )}
                        <div className="ml-2">
                            <div className={`info-main ${invoice.status === 'rejected' && 'text-danger'}`}>
                                {t(`influencer.invoice.statuses.${invoice?.status ?? '-'}`)}
                            </div>
                            <div>{t('influencer.invoice.status')}</div>
                        </div>
                    </div>
                </div>
                <div className="card-info">
                    <div className="info-block">
                        <div className="info-main">
                            {!!invoice?.invoice?.date ? formatDate(invoice?.invoice?.date) : '-'}
                        </div>
                        <div>{t('influencer.invoice.upload_date')}</div>
                    </div>
                </div>
                <div className="card-info">
                    <div className="info-block">
                        <div className="info-main euro-symbol">{priceSplitter(invoice?.price ?? 0)}</div>
                        <div>{t('influencer.invoice.invoiced')}</div>
                    </div>
                </div>
                <div className="card-info">
                    <div className="info-block text-end">
                        {showDropdown && (
                            <Dropdown className="btn-group position-static mr-2" drop="down">
                                <Dropdown.Toggle variant="light" id="dropdown-basic" aria-label="Actions">
                                    <FaEllipsisV className="text-secondary" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>{dropdownButtonType(invoice?.status ?? '')}</Dropdown.Menu>
                            </Dropdown>
                        )}
                        <div className="invoice-main-action d-inline-block">
                            {mainButtonType(invoice?.status ?? '')}
                        </div>
                    </div>
                </div>
            </CardLayout>
        </>
    );
};

export default InvoiceSubCard;
